import {call, put, select, take, takeLatest} from 'redux-saga/effects';

import {isEqual} from 'lodash';
import {action, Action} from '../../helpers';
import {licenseSelectors} from '../../license';
import {settingsActions, settingsSelectors, settingsTypes} from '..';
import {asyncSagaConstructor} from '../../common';
import api from '../../../api';
import ApiService from '../../../api/ApiService';
import {layoutActions, layoutTypes} from '../../../features/layout/redux';
import {DialogResult, DialogTypes} from '../../../common/names';
import {DiplomatFeature} from '../../../types/DiplomatFeature';
import {openDialogWithResult} from 'redux/common/saga/openDialogWithResult';
import {ICloseSubDialogAction} from 'features/layout/redux/actions';
import {auditArchiveRunning} from './auditArchiveRunning';
import {IBooleanResult} from 'types/diplomat/BooleanResult';
import {closeDialog} from 'redux/common/saga/closeDialogSaga';
import {pushSuccessToast} from 'features/notifications/redux/actions';
import i18n from 'i18n';
import {createAmdinRoleSaga} from 'redux/settings/sagas/createAmdinRole';
import {deleteAmdinRoleSaga} from './deleteAmdinRole';
import {IAdminRoles} from 'types/diplomat/settings/adminroles/AdminRoles';
import {castToSend} from 'common/yup/castToSend';
import {AdminRole} from 'types/diplomat/settings/adminroles/AdminRole';

function* saveAuditTrailSettingsSaga(act: Action) {
   try {
      const {payload, callbacks} = act;
      const isFeatureSupported = (yield select(licenseSelectors.isFeatureSupported)) as ReturnType<
         typeof licenseSelectors.isFeatureSupported
      >;
      const prevSettings = (yield select(settingsSelectors.auditTrailSettings)) as ReturnType<
         typeof settingsSelectors.auditTrailSettings
      >;

      const values = {
         ...payload,
         dbSettings: {...payload.dbSettings},
      };
      values.archive = !values.archive;
      values.archiveByAge = values.archiveBy === 'archiveByAge' ? true : false;
      values.archiveByRecords = !values.archiveByAge;
      if (values.dbSettings) {
         values.dbSettings.useWindowsAuthentication =
            values.authentication === 'Windows' ? true : false;
      }
      delete values.authentication;
      delete values.archiveBy;

      const res = (yield call(auditArchiveRunning)) as ReturnType<typeof auditArchiveRunning>;
      if (res) {
         return;
      }

      if (
         isFeatureSupported(DiplomatFeature.JOB_SUSPENSION) &&
         (values.suspended !== prevSettings.suspended ||
            !isEqual(values.dbSettings, prevSettings.dbSettings))
      ) {
         // console.debug(values, prevSettings);

         const {result: allSuspended} = (yield call(
            asyncSagaConstructor(api.suspendAll, layoutTypes.GET_SUSPEND_ALL)
         )) as IBooleanResult;
         if (!allSuspended) {
            // openAuditDBWarningDialog();
            // yield put(layoutActions.openSubDialog(DialogTypes.AuditWarningDialog.id));
            // const result: Action = yield take(layoutTypes.CLOSE_SUB_DIALOG);
            const result: ICloseSubDialogAction<string> = yield call(openDialogWithResult, {
               id: DialogTypes.AuditWarningDialog.id,
            });

            // console.debug('Suspend', result);
            if (result?.payload?.data) {
               if (result?.payload.data === DialogResult.AUDIT_SUSPEND) {
                  yield put(layoutActions.openSubDialog(DialogTypes.AuditSuspensionDialog.id));
                  // wait for susspension succeeded
                  yield take(layoutTypes.SUSPEND_TRANSACTION_FOLDER.SUCCESS);
                  yield put(layoutActions.closeSubDialog(DialogTypes.AuditSuspensionDialog.id));
               }
            } else {
               // user clicked cancel
               return;
            }
         }
      }

      //SAVE settings
      yield call(
         asyncSagaConstructor(api.auditTrailSettings, settingsTypes.POST_AUDIT_TRAIL_SETTINGS, {
            getMethod: ApiService.postSecureJson,
            data: values,
            onSuccess: (res) => {
               callbacks?.setSubmitting(false);
               callbacks?.resetForm({
                  values: res,
               });
            },
            onError: (e) => {
               callbacks?.setSubmitting(false);
               callbacks?.setErrors(e);
            },
         })
      );

      yield call(closeDialog, DialogTypes.SettingsAuditTrailDialog.id);
      yield put(pushSuccessToast(i18n.t('Audit Trail Settings Updated')));
   } catch (e) {
      console.error(e);
      if (act.callbacks && act.callbacks.setErrors) {
         yield call(act.callbacks.setErrors, (e as any)?.errors);
      }
   } finally {
      if (act.callbacks && act.callbacks.setSubmitting) {
         yield call(act.callbacks.setSubmitting, false);
      }
   }
}

function* updateAdminRolesSaga(act: Action<IAdminRoles>) {
   try {
      if (!act.payload?.roles) return;

      for (let role of act.payload.roles) {
         if (role.immutable) continue;

         yield call(
            asyncSagaConstructor(api.adminroles, settingsTypes.UPDATE_ADMIN_ROLES, {
               getMethod: ApiService.postSecureJson,
               data: JSON.stringify(castToSend(AdminRole, role)),
               // onSuccess: (res) => {
               //    callbacks?.setSubmitting(false);
               //    callbacks?.resetForm({
               //       values: res,
               //    });
               // },
               // onError: (e) => {
               //    callbacks?.setSubmitting(false);
               //    callbacks?.setErrors(e);
               // },
            })
         );
      }
      yield put(pushSuccessToast(i18n.t('Admin Roles succesfully updated')));
   } catch (e) {
      console.error(e);
   } finally {
      try {
         yield call(asyncSagaConstructor(api.adminroles, settingsTypes.FETCH_ADMIN_ROLES));
      } catch (e) {
         console.error(e);
      }
   }
}

export function* settingsSaga() {
   yield takeLatest(settingsTypes.SAVE_AUDIT_TRAIL_SETTINGS.SAGA, saveAuditTrailSettingsSaga);
   yield takeLatest(settingsTypes.CREATE_ADMIN_ROLE.SAGA, createAmdinRoleSaga);
   yield takeLatest(settingsTypes.DELETE_ADMIN_ROLE.SAGA, deleteAmdinRoleSaga);
   yield takeLatest(settingsTypes.UPDATE_ADMIN_ROLES.SAGA, updateAdminRolesSaga);
}
